import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../../../../hooks/useToast';
import { useScenarioFieldConfig } from '../../../../hooks/usePartnerBranding';
import type { GenerationModel } from '../../../../features/generation-models/api';
import {
  useGenerationState,
  GENERATION_MODEL_SPEED,
  GENERATION_MODEL_LITE,
  DEFAULT_SCENARIO_MODEL,
  SCENARIO_MODEL_LEGACY_MAP,
  resolveLegacyImageModel,
  readStoredThinkingLevel,
  type ThinkingLevel,
} from '../../state/useGenerationState';
import { resolveImageSizeFromResolution } from './useCreditCalculations';
import { castingCapFromCharacterCount, modelCastActorCap } from '../utils';
import {
  STORAGE_KEY_AUTO_GENERATE_FIRST_FRAME,
  STORAGE_KEY_AUTO_GENERATE_STORYBOARD,
  STORAGE_KEY_AUTO_GENERATE_ENDCARD_FRAME,
  STORAGE_KEY_SCENARIO_COUNT,
  STORAGE_KEY_GENERATION_MODEL,
  STORAGE_KEY_SCENARIO_MODEL,
  STORAGE_KEY_TONE,
  STORAGE_KEY_DURATION,
  STORAGE_KEY_INCLUDE_NARRATION,
  STORAGE_KEY_INCLUDE_TELOP,
  STORAGE_KEY_INCLUDE_DIALOGUE,
  STORAGE_KEY_EXCLUDE_PEOPLE,
} from '../constants';
import { isCustomDurationValue } from '../customDuration';

export type FrameMode = 'first_only' | 'first_and_last' | 'first_with_next';
// 思考レベルの型は modelCatalog が正本 (既存の import 元互換のため再エクスポート)
export type { ThinkingLevel } from '../../state/modelCatalog';

interface ScenarioFieldConfig {
  max_scenario_count: number;
  show_narration: boolean;
  show_dialogue: boolean;
  show_telop: boolean;
}

/** 旧 promptOptions のうち想定尺の選択肢だけを使う (ツリー無効時のフォールバック)。 */
type PromptOptionsLike = {
  duration?: { options: { value: string; label: string }[] };
} | undefined;

export interface UseScenarioFormStateParams {
  /** 旧 API 由来の選択肢 (想定尺のツリー無効時フォールバック)。 */
  promptOptions: PromptOptionsLike;
  /** 動画モデル一覧。想定尺の既定値をモデル最大尺に追従させるのに使う。 */
  videoGenerationModels: GenerationModel[];
  /**
   * 現在のフェーズ (setup/work) を保持する ref。想定尺の既定スナップは新規作成
   * (setup) のみで行うため、レンダー跨ぎの最新値を ref で参照する。
   */
  phaseRef: React.MutableRefObject<'setup' | 'work'>;
}

export interface ScenarioFormState {
  // partner (OEM) 設定
  scenarioFieldConfig: ScenarioFieldConfig;

  // シナリオ本数
  scenarioCount: number;
  setScenarioCount: Dispatch<SetStateAction<number>>;
  scenarioCountOptions: number[];

  // 追加プロンプト
  additionalPrompt: string;
  setAdditionalPrompt: Dispatch<SetStateAction<string>>;

  // トーン
  tone: string;
  setTone: Dispatch<SetStateAction<string>>;

  // 想定尺
  duration: string;
  setDuration: Dispatch<SetStateAction<string>>;
  /**
   * ユーザーがプルダウンで想定尺を明示的に変更したか。true の間は既定スナップ
   * (モデル追従) の抑止判定に使う。正規化 effect・DB 復元・既定スナップなど
   * programmatic な setDuration では立てない。
   */
  durationUserEditedRef: React.MutableRefObject<boolean>;
  durationSelectOptions: { value: string; label: string; metadata?: Record<string, unknown> | null }[];
  /** 想定尺の既定 option value (動画モデル最大尺に追従)。 */
  modelAwareDurationDefault: string;
  /** 選択中動画モデルの最大対応尺 (秒)。supported_durations 未ロード時は 0。
   *  想定尺 > 最大尺のときの分割生成の注意アイコン (ScenarioSettingsSection) に使う。 */
  modelMaxDuration: number;
  /** 選択中動画モデルの定義行 (generation_models)。未ロード/未発見時は null。
   *  分割生成ツールチップのモデル表示名などに使う。 */
  selectedVideoModelDef: GenerationModel | null;

  // 登場人物数
  characterCount: string;
  /** ユーザー操作としてマークする setter (超過俳優の自動解除トリガー)。 */
  setCharacterCount: Dispatch<SetStateAction<string>>;
  /** programmatic な復元用 setter (ユーザー操作としてマークしない)。 */
  setCharacterCountRaw: Dispatch<SetStateAction<string>>;
  characterCountUserEditedScenarioRef: React.MutableRefObject<string | null>;
  characterCountSelectOptions: { value: string; label: string }[];

  // ナレ/セリフ/テロップ
  includeNarration: string;
  setIncludeNarration: Dispatch<SetStateAction<string>>;
  includeTelop: string;
  setIncludeTelop: Dispatch<SetStateAction<string>>;
  includeDialogue: string;
  setIncludeDialogue: Dispatch<SetStateAction<string>>;

  // 自動生成トグル / フレームモード
  autoGenerateFirstFrame: boolean;
  setAutoGenerateFirstFrame: Dispatch<SetStateAction<boolean>>;
  autoGenerateStoryboard: boolean;
  setAutoGenerateStoryboard: Dispatch<SetStateAction<boolean>>;
  autoGenerateEndcardFrame: boolean;
  setAutoGenerateEndcardFrame: Dispatch<SetStateAction<boolean>>;
  lastFrameMode: FrameMode;
  setLastFrameMode: Dispatch<SetStateAction<FrameMode>>;

  // モデル選択
  scenarioGenerationModel: string;
  setScenarioGenerationModel: Dispatch<SetStateAction<string>>;
  selectScenarioImageModel: (modelCode: string) => void;
  thinkingLevel: ThinkingLevel;
  setThinkingLevel: Dispatch<SetStateAction<ThinkingLevel>>;
  scenarioModel: string;
  setScenarioModel: Dispatch<SetStateAction<string>>;

  // 人物を含めない
  excludePeople: boolean;
  setExcludePeople: Dispatch<SetStateAction<boolean>>;

  // キャスティングフィルター
  castingGenderFilter: string;
  setCastingGenderFilter: Dispatch<SetStateAction<string>>;
  castingAgeFilter: number | null;
  setCastingAgeFilter: Dispatch<SetStateAction<number | null>>;
  /** AI俳優一覧の人種フィルタ ('none' | 'jp' | 'foreigner' | 'character')。
   *  Seedance プリセット表示中は概念自体がないため CastingSection 側で非表示にする。 */
  castingEthnicityFilter: string;
  setCastingEthnicityFilter: Dispatch<SetStateAction<string>>;

  // プロンプト入力モードの本文
  videoPrompt: string;
  setVideoPrompt: Dispatch<SetStateAction<string>>;
}

/**
 * シナリオ設定フォームの state を 1 箇所に集約したフック。
 *
 * ScenarioGenerator に散在していたフォーム系 useState と、その localStorage
 * 初期化・相互整合 effect (想定尺の正規化 / 既定スナップ、ナレ/セリフ/テロップの
 * legacy→tree 移行、登場人物数変更時の超過俳優解除) をそのまま移設したもの。
 * 永続化 (書き込み) は従来どおり useLocalStorageSync が担当する。
 */
export function useScenarioFormState({
  promptOptions,
  videoGenerationModels,
  phaseRef,
}: UseScenarioFormStateParams): ScenarioFormState {
  const toast = useToast();
  const {
    activeScenarioId,
    additionalCastings,
    setAdditionalCastings,
    additionalSeedancePresetActors,
    setAdditionalSeedancePresetActors,
    videoSettings,
    durationTreeOptions,
    durationTreeEnabled,
    characterCountTreeOptions,
    characterCountTreeEnabled,
    narrationTreeOptions,
    narrationTreeEnabled,
    dialogueTreeOptions,
    dialogueTreeEnabled,
    telopTreeOptions,
    telopTreeEnabled,
  } = useGenerationState();

  // partner (OEM) が設定した UI 表示制御 / シナリオ案最大数。
  // 未取得時はデフォルト (max=5, 全フィールド表示) にフォールバックする。
  const scenarioFieldConfig = useScenarioFieldConfig();

  const [scenarioCount, setScenarioCount] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_SCENARIO_COUNT);
    return saved ? Number.parseInt(saved) : 2;
  });

  // partner の max_scenario_count に合わせて選択肢と現在値を調整する
  const scenarioCountOptions = useMemo(() => {
    const max = Math.max(1, Math.min(25, scenarioFieldConfig.max_scenario_count ?? 5));
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [scenarioFieldConfig.max_scenario_count]);

  useEffect(() => {
    const max = scenarioCountOptions[scenarioCountOptions.length - 1] ?? 1;
    if (scenarioCount > max) setScenarioCount(max);
  }, [scenarioCountOptions, scenarioCount]);

  const [additionalPrompt, setAdditionalPrompt] = useState('');

  const [tone, setTone] = useState(() => localStorage.getItem(STORAGE_KEY_TONE) || '');

  // 前回プルダウンで選んだ想定尺を復元する。復元値は明示編集扱いにして既定スナップ
  // (モデル追従) で上書きさせない。モデル最大尺を超えていても保存値を優先する
  // (分割生成になる旨は想定尺横の注意アイコンで示す)。
  const [savedDuration] = useState(() => localStorage.getItem(STORAGE_KEY_DURATION));
  const [duration, setDuration] = useState(() => savedDuration ?? '15秒');
  const durationUserEditedRef = useRef(savedDuration !== null);
  const durationRestoreValidationPendingRef = useRef(savedDuration !== null);

  // 想定尺が新ツリー駆動のとき、旧 value / ロード復元のラベル("15秒"等)を対応する
  // option key へ正規化する（プルダウンの選択表示と key 送信を一致させる）。
  useEffect(() => {
    if (!durationTreeEnabled || durationTreeOptions.length === 0) return;
    if (!duration || durationTreeOptions.some((o) => o.value === duration)) return;
    const match = durationTreeOptions.find((o) => o.label === duration);
    if (match) setDuration(match.value);
  }, [durationTreeEnabled, durationTreeOptions, duration]);

  // 想定尺プルダウンの選択肢: ツリー有効時は属性ツリー由来 (metadata 込み)、無効時は旧 promptOptions。
  const durationSelectOptions = useMemo<{ value: string; label: string; metadata?: Record<string, unknown> | null }[]>(() => {
    if (durationTreeEnabled) return durationTreeOptions;
    return promptOptions?.duration?.options.map((o) => ({ value: o.value, label: o.label })) ?? [];
  }, [durationTreeEnabled, durationTreeOptions, promptOptions?.duration]);

  // 登場人物数。value は option key('cc_2' 等)。既定は '' (=指定なし。人数指示を出さない)。
  const [characterCount, setCharacterCountState] = useState('');
  // work フェーズの DB 復元 effect が属性ツリー読み込み後に遅れて走ることがある。
  // ユーザーが人数を変更済みなら、保存値なしの復元で '' に戻して選択上限を 1 にしない。
  const characterCountUserEditedScenarioRef = useRef<string | null>(null);
  // 追加俳優の自動解除は、ユーザーが登場人物数プルダウンを明示的に変更した時だけ行う。
  // 生成直後・DB復元・属性ツリー後着による programmatic な characterCount 変更では解除しない。
  const characterCountUserChangePendingRef = useRef(false);
  const setCharacterCount = useCallback<Dispatch<SetStateAction<string>>>((next) => {
    setCharacterCountState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      const changed = resolved !== prev;
      if (changed) {
        characterCountUserEditedScenarioRef.current = activeScenarioId;
        characterCountUserChangePendingRef.current = true;
      }
      return resolved;
    });
  }, [activeScenarioId]);
  // 選択肢: ツリー有効時のみ属性ツリー由来。無効時は空 (UI 側で行ごと非表示)。
  const characterCountSelectOptions = useMemo<{ value: string; label: string }[]>(() => {
    return characterCountTreeEnabled ? characterCountTreeOptions : [];
  }, [characterCountTreeEnabled, characterCountTreeOptions]);

  // 複数AI俳優対応: ユーザーが登場人物数(上限)を変更した結果、超過した追加俳優だけを
  // 末尾から自動解除して警告する。主俳優(selectedCasting)は cap>=1 のため常に維持。
  // ⚠️ 俳優選択(additionalCastings 変化)や DB 復元では発火させない。
  const prevCastingCapRef = useRef<number | null>(null);
  useEffect(() => {
    // 実効上限 = min(登場人物数, モデル別キャスト上限)。CastingSection の選択制限と揃える。
    const cap = Math.min(
      castingCapFromCharacterCount(characterCount, characterCountSelectOptions),
      modelCastActorCap(videoSettings.videoModel, videoGenerationModels),
    );
    const prevCap = prevCastingCapRef.current;
    prevCastingCapRef.current = cap;
    const isUserCharacterCountChange = characterCountUserChangePendingRef.current;
    characterCountUserChangePendingRef.current = false;
    // 生成後/復元時の cap 低下は保存値・属性ツリー到着順の揺れで起こりうるため、
    // 俳優 state は触らない。選択上限の強制解除はユーザー操作にだけ紐づける。
    if (prevCap === null || !isUserCharacterCountChange) return;
    const maxAdditional = Math.max(0, cap - 1);
    let trimmed = false;
    if (additionalCastings.length > maxAdditional) {
      setAdditionalCastings((prev) => prev.slice(0, maxAdditional));
      trimmed = true;
    }
    if (additionalSeedancePresetActors.length > maxAdditional) {
      setAdditionalSeedancePresetActors((prev) => prev.slice(0, maxAdditional));
      trimmed = true;
    }
    if (trimmed) {
      toast.show(`登場人物数を${cap}人に変更したため、超過したAI俳優の選択を解除しました`);
    }
  }, [characterCount, characterCountSelectOptions, videoSettings.videoModel, videoGenerationModels, additionalCastings, setAdditionalCastings, additionalSeedancePresetActors, setAdditionalSeedancePresetActors, toast, activeScenarioId]);

  // 動画モデル切替でキャスト上限 (modelCastActorCap) が縮んだとき、超過した追加俳優を
  // 末尾から自動解除して警告する。DB 復元では俳優 state を触らない方針 (上の effect と同じ)
  // のため、新規作成 (setup フェーズ) でのモデル変更に限定する (復元は work フェーズに着地する)。
  const prevCastVideoModelRef = useRef<string | null>(null);
  useEffect(() => {
    const model = videoSettings.videoModel;
    const prevModel = prevCastVideoModelRef.current;
    prevCastVideoModelRef.current = model;
    if (prevModel === null || prevModel === model) return;
    if (phaseRef.current !== 'setup') return;
    const cap = Math.min(
      castingCapFromCharacterCount(characterCount, characterCountSelectOptions),
      modelCastActorCap(model, videoGenerationModels),
    );
    const maxAdditional = Math.max(0, cap - 1);
    let trimmed = false;
    if (additionalCastings.length > maxAdditional) {
      setAdditionalCastings((prev) => prev.slice(0, maxAdditional));
      trimmed = true;
    }
    if (additionalSeedancePresetActors.length > maxAdditional) {
      setAdditionalSeedancePresetActors((prev) => prev.slice(0, maxAdditional));
      trimmed = true;
    }
    if (trimmed) {
      toast.show(`選択中の動画モデルは俳優を最大${cap}人まで参照できるため、超過した選択を解除しました`);
    }
  }, [videoSettings.videoModel, videoGenerationModels, characterCount, characterCountSelectOptions, additionalCastings, setAdditionalCastings, additionalSeedancePresetActors, setAdditionalSeedancePresetActors, toast, phaseRef]);

  const [videoPrompt, setVideoPrompt] = useState('');

  // 「人物を含めない」モード。ON でシナリオ生成プロンプトに人物を登場させない指示を追加する。
  // 最後の選択を localStorage に保存し、次回起動時に復元する。
  const [excludePeople, setExcludePeople] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_EXCLUDE_PEOPLE);
    return saved !== null ? saved === 'true' : false;
  });

  // キャスティングフィルター状態（性別・年代・人種の指定）
  const [castingGenderFilter, setCastingGenderFilter] = useState<string>('none');
  const [castingAgeFilter, setCastingAgeFilter] = useState<number | null>(null);
  const [castingEthnicityFilter, setCastingEthnicityFilter] = useState<string>('none');

  const [autoGenerateFirstFrame, setAutoGenerateFirstFrame] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_AUTO_GENERATE_FIRST_FRAME);
    return saved !== null ? saved === 'true' : false; // デフォルトはfalse
  });
  // 「ストーリーボードを自動生成」: ストーリーボードモードでシナリオ生成完了後、
  // 完了したシナリオから順に絵コンテ静止画を自動生成する (1枚/最初のフレームのみ/
  // ロゴ・商品画像・絵コンテイラストは可能なものを On)。
  const [autoGenerateStoryboard, setAutoGenerateStoryboard] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_AUTO_GENERATE_STORYBOARD);
    return saved !== null ? saved === 'true' : false; // デフォルトはfalse
  });
  const [autoGenerateEndcardFrame, setAutoGenerateEndcardFrame] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_AUTO_GENERATE_ENDCARD_FRAME);
    return saved !== null ? saved === 'true' : false; // デフォルトはfalse
  });
  // 2026-08-31: フレームモード (最初のみ/最初と最後/次シーン連結) の3択 UI は
  // フォームから撤去し、新規シナリオは常に 'first_only' を送る固定値にした
  // (setLastFrameMode の呼び出し元が無くなったため、実質的にこの初期値で固定される)。
  // 既存シナリオに保存済みの first_and_last / first_with_next は、この state とは
  // 無関係に scenario.frame_mode (DB 正本) から読み込まれるため、読み込み・編集画面
  // ・生成は従来どおり動く (ScenarioInfoModal.tsx の編集 UI も現状維持)。
  // localStorage (STORAGE_KEY_AUTO_APPEND_TO_PREVIOUS_SCENE) の旧値はもう読まない。
  const [lastFrameMode, setLastFrameMode] = useState<FrameMode>('first_only');
  // 画像生成モデルの既定は Google Gemini「標準」(GENERATION_MODEL_SPEED。2026-10-08 に
  // gemini-3.1-flash-image-preview → gemini-nano-banana-2.1 へ切替)。
  // 2026-08-31: シナリオフォームのみ既定を GPT Image 2 自動 (DEFAULT_GENERATION_MODEL、
  // 静止画生成と共有の全体既定) から Gemini 標準へ変更 (BE 側の既定は変更していない)。
  const [scenarioGenerationModel, setScenarioGenerationModel] = useState<string>(() => {
    // 廃止済みモデル (gpt-image-2-*) の旧値は同品質の現行コードへ読み替える
    const saved = resolveLegacyImageModel(localStorage.getItem(STORAGE_KEY_GENERATION_MODEL));
    return saved || GENERATION_MODEL_SPEED;
  });

  // 選択中動画モデルの定義行。未ロード/未発見時は null。
  const selectedVideoModelDef = useMemo(
    () => videoGenerationModels.find((m) => m.model_code === videoSettings.videoModel) ?? null,
    [videoGenerationModels, videoSettings.videoModel],
  );

  // 選択中動画モデルの最大尺。supported_durations 未ロード時は 0。
  const modelMaxDuration = useMemo(() => {
    const list = (selectedVideoModelDef as { supported_durations?: number[] | null } | null)?.supported_durations;
    return Array.isArray(list) && list.length > 0 ? Math.max(...list) : 0;
  }, [selectedVideoModelDef]);

  // 想定尺の既定 option value。基本は 15秒だが、モデル最大が15秒未満(Omni=10)なら
  // その最大以下で最大の想定尺 option を既定にする(該当が無ければ最小 option / '15秒' へフォールバック)。
  const modelAwareDurationDefault = useMemo(() => {
    const target = modelMaxDuration > 0 ? Math.min(15, modelMaxDuration) : 15;
    const withSec = durationSelectOptions
      .map((o) => ({ ...o, sec: Number(/(\d+)\s*秒/.exec(o.label)?.[1] ?? NaN) }))
      .filter((o) => Number.isFinite(o.sec));
    const eligible = withSec.filter((o) => o.sec <= target).sort((a, b) => b.sec - a.sec);
    const fallback = withSec.find((o) => o.label === '15秒') ?? withSec[0];
    return (eligible[0] ?? fallback)?.value ?? '15秒';
  }, [durationSelectOptions, modelMaxDuration]);

  // 復元した想定尺が現在の選択肢に無ければ (属性ツリーの変更等) 捨てて既定スナップへ任せる。
  // 範囲内のカスタム秒数 (「45秒」・customDuration.ts) は選択肢に無くても残す。
  // 既定スナップ effect より前に宣言し、同じ flush で既定値へ寄るようにする。
  useEffect(() => {
    if (!durationRestoreValidationPendingRef.current) return;
    if (durationSelectOptions.length === 0) return;
    durationRestoreValidationPendingRef.current = false;
    if (duration === '' || durationSelectOptions.some((o) => o.value === duration || o.label === duration)) return;
    if (isCustomDurationValue(durationSelectOptions, duration)) return;
    durationUserEditedRef.current = false;
    localStorage.removeItem(STORAGE_KEY_DURATION);
  }, [durationSelectOptions, duration]);

  // ユーザー未編集かつ setup フェーズ(新規作成)のとき、想定尺をモデル追従の既定へ寄せる。
  // これにより Omni を選択中の起動時は既定が最大10秒以内に収まる。
  // 明示編集(handleDurationChange)後・編集画面(work)では触らない。
  useEffect(() => {
    if (durationUserEditedRef.current) return;
    if (phaseRef.current === 'work') return;
    if (!modelAwareDurationDefault) return;
    if (duration === modelAwareDurationDefault) return;
    setDuration(modelAwareDurationDefault);
  }, [modelAwareDurationDefault, duration, phaseRef]);

  const selectScenarioImageModel = useCallback((modelCode: string) => {
    setScenarioGenerationModel(modelCode);
    localStorage.setItem(STORAGE_KEY_GENERATION_MODEL, modelCode);
  }, []);

  // Gemini スピード (ライト/gemini-3.1-flash-lite-image) は 1K 固定 (2K/4K の
  // credit_rate_config 行が無い) モデル。解像度が 1K 以外 (720p 以外、= 1080p 以上) に
  // 変わったら Gemini 標準へ自動切替してトーストで知らせる。videoSettings.resolution の
  // 変更経路 (このフォーム内で解像度を変えられるのは動画生成設定アコーディオンのみ) に
  // 配線するため、ここでは videoSettings.resolution を直接 watch する。
  // 初回マウントで誤発火しないよう、上の動画モデル切替 effect と同じ ref 比較パターンを使う。
  const prevResolutionForLiteRef = useRef<string | undefined>(videoSettings.resolution);
  useEffect(() => {
    const prevResolution = prevResolutionForLiteRef.current;
    const currentResolution = videoSettings.resolution;
    prevResolutionForLiteRef.current = currentResolution;
    if (prevResolution === currentResolution) return;
    // scenarioGenerationModel はシナリオ作成フォーム (setup フェーズ) の既定値。生成後の
    // 編集画面 (work フェーズ) では意味を持たない (フレーム別モデルは別state) ため対象外。
    if (phaseRef.current !== 'setup') return;
    if (scenarioGenerationModel !== GENERATION_MODEL_LITE) return;
    if (resolveImageSizeFromResolution(currentResolution) === '1K') return;
    selectScenarioImageModel(GENERATION_MODEL_SPEED);
    toast.show('Geminiスピードは1K解像度専用のため、Gemini標準に変更しました');
  }, [videoSettings.resolution, scenarioGenerationModel, selectScenarioImageModel, toast, phaseRef]);
  const [thinkingLevel, setThinkingLevel] = useState<ThinkingLevel>(readStoredThinkingLevel);
  const [scenarioModel, setScenarioModel] = useState<string>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_SCENARIO_MODEL);
    if (!saved) return DEFAULT_SCENARIO_MODEL;
    // Phase 1: 旧 Gemini model id を新 id に正規化 (docs/features/gemini-lineup-refresh-2026q2.md)
    const migrated = SCENARIO_MODEL_LEGACY_MAP[saved];
    if (migrated) {
      localStorage.setItem(STORAGE_KEY_SCENARIO_MODEL, migrated);
      return migrated;
    }
    return saved;
  });

  // フェーズ6: tree 有効時は option key('narration_on'/'conversation' 等)、無効時は legacy 値
  // ('unspecified'|'include'|'exclude'(|'generate_only'))を保持する。'unspecified' は両モード
  // 共通の「指定なし」センチネル(localStorage 互換)。
  const [includeNarration, setIncludeNarration] = useState<string>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_INCLUDE_NARRATION);
    // 初回は「指定なし」をデフォルト表示
    return saved || 'unspecified';
  });
  const [includeTelop, setIncludeTelop] = useState<string>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_INCLUDE_TELOP);
    // 初回は「あり（後付け）」をデフォルト表示
    return saved || 'generate_only';
  });
  const [includeDialogue, setIncludeDialogue] = useState<string>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_INCLUDE_DIALOGUE);
    return saved || 'unspecified';
  });
  // tree 有効化時、localStorage 由来の legacy 値('include'/'generate_only'等)を対応する
  // option key へ移行する(フェーズ6)。mode 経由でマップするため、テロップ既定の「あり(後付け)」
  // (generate_only)が tree key 'telop_generate' に保たれる(潰して指定なしにしない)。
  useEffect(() => {
    const LEGACY_TO_MODE: Record<string, string> = {
      include: 'include', exclude: 'none', generate_only: 'generate_only',
    };
    const normalize = (
      enabled: boolean,
      opts: { value: string; mode: string | null }[],
      value: string,
      setter: React.Dispatch<React.SetStateAction<string>>,
    ) => {
      if (!enabled || value === 'unspecified') return;
      if (opts.some((o) => o.value === value)) return; // 既に有効な tree key
      const mode = LEGACY_TO_MODE[value];
      const opt = mode ? opts.find((o) => o.mode === mode) : undefined;
      setter(opt ? opt.value : 'unspecified');
    };
    normalize(narrationTreeEnabled, narrationTreeOptions, includeNarration, setIncludeNarration);
    normalize(dialogueTreeEnabled, dialogueTreeOptions, includeDialogue, setIncludeDialogue);
    normalize(telopTreeEnabled, telopTreeOptions, includeTelop, setIncludeTelop);
  }, [narrationTreeEnabled, narrationTreeOptions, dialogueTreeEnabled, dialogueTreeOptions, telopTreeEnabled, telopTreeOptions, includeNarration, includeDialogue, includeTelop]);

  return {
    scenarioFieldConfig,
    scenarioCount,
    setScenarioCount,
    scenarioCountOptions,
    additionalPrompt,
    setAdditionalPrompt,
    tone,
    setTone,
    duration,
    setDuration,
    durationUserEditedRef,
    durationSelectOptions,
    modelAwareDurationDefault,
    modelMaxDuration,
    selectedVideoModelDef,
    characterCount,
    setCharacterCount,
    setCharacterCountRaw: setCharacterCountState,
    characterCountUserEditedScenarioRef,
    characterCountSelectOptions,
    includeNarration,
    setIncludeNarration,
    includeTelop,
    setIncludeTelop,
    includeDialogue,
    setIncludeDialogue,
    autoGenerateFirstFrame,
    setAutoGenerateFirstFrame,
    autoGenerateStoryboard,
    setAutoGenerateStoryboard,
    autoGenerateEndcardFrame,
    setAutoGenerateEndcardFrame,
    lastFrameMode,
    setLastFrameMode,
    scenarioGenerationModel,
    setScenarioGenerationModel,
    selectScenarioImageModel,
    thinkingLevel,
    setThinkingLevel,
    scenarioModel,
    setScenarioModel,
    excludePeople,
    setExcludePeople,
    castingGenderFilter,
    setCastingGenderFilter,
    castingAgeFilter,
    setCastingAgeFilter,
    castingEthnicityFilter,
    setCastingEthnicityFilter,
    videoPrompt,
    setVideoPrompt,
  };
}
